import type { MaybeRefOrGetter } from 'vue';
import { createEventHook, createSharedComposable, tryOnScopeDispose } from '@vueuse/core';
import { computed, nextTick, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { useNavigationGuard } from '~/composables/use-navigation-guard';

/**
 * @internal
 */
export function useGlobalEditGuard() {
	const discardHook = createEventHook();

	const { t } = useI18n();
	const router = useRouter();
	let skipGuard = false;
	let dialogIsOpen = false;

	const hasChangesGetters = ref<MaybeRefOrGetter<boolean>[]>([]);
	const hasChanges = computed(() => hasChangesGetters.value.some((getter) => toValue(getter)));

	const trackHasChanges = (getter: MaybeRefOrGetter<boolean>) => {
		hasChangesGetters.value.push(getter);
		return () => hasChangesGetters.value = hasChangesGetters.value.filter((g) => g !== getter);
	};

	useNavigationGuard(
		hasChanges,
		(to) => {
			if (hasChanges.value && !skipGuard) {
				nextTick(async () => {
					if (!dialogIsOpen) {
						dialogIsOpen = true;
						const keepEditing = await showConfirmationDialog({
							props: {
								title: t('navigation_guard.unsaved_changes.title'),
								description: t('navigation_guard.unsaved_changes.description'),
								confirm: {
									label: t('navigation_guard.unsaved_changes.keep_editing'),
									color: 'primary',
								},
								cancel: {
									label: t('navigation_guard.unsaved_changes.discard'),
								},
								close: false,
								dismissible: false,
							},
						});

						if (!keepEditing) {
							discardHook.trigger();
							skipGuard = true;

							router.push(to);
						}

						dialogIsOpen = false;
					}
				});

				return false;
			}

			skipGuard = false;
		},
	);

	return {
		onDiscard: discardHook.on,
		trackHasChanges,
	};
}

const useSharedGlobalEditGuard = createSharedComposable(useGlobalEditGuard);

export function useEditGuard(
	hasChanges: MaybeRefOrGetter<boolean>,
	options?: { onDiscard?: () => void },
) {
	const { onDiscard, trackHasChanges } = useSharedGlobalEditGuard();

	const disposeTracker = trackHasChanges(hasChanges);
	const { off: disposeDiscardListener } = onDiscard(() => options?.onDiscard?.());

	const dispose = () => {
		disposeTracker();
		disposeDiscardListener?.();
	};

	tryOnScopeDispose(dispose);
}
